import { useState, useMemo } from 'react'
import { differenceInDays, parseISO } from 'date-fns'
import { useMedications } from './hooks/useMedications'
import { useAuth } from '../../hooks/useAuth'
import { useMembers } from '../../hooks/useMembers'
import { MedicationForm } from './components/MedicationForm'
import { MedicationDetail } from './components/MedicationDetail'
import { SkeletonList } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/ui/EmptyState'
import { cn } from '../../lib/utils'

const RefillBadge = ({ refillDate }) => {
  if (!refillDate) return null
  const days = differenceInDays(parseISO(refillDate), new Date())
  if (days < 0)  return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-600">Refill overdue</span>
  if (days <= 7) return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Refill in {days}d</span>
  return null
}

const MedicationCard = ({ medication, onClick }) => {
  const needsRefill = medication.refill_date &&
    differenceInDays(parseISO(medication.refill_date), new Date()) <= 7

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left bg-surface rounded-xl border px-4 py-3 hover:border-primary/30 transition-colors',
        needsRefill ? 'border-amber-300' : 'border-border',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-sm text-text-primary truncate">{medication.name}</p>
          <p className="text-xs text-text-muted mt-0.5">
            {medication.dosage} · {medication.frequency?.replace(/_/g, ' ')}
          </p>
          {medication.profiles && (
            <p className="text-xs text-text-muted mt-0.5">{medication.profiles.display_name}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {!medication.is_active && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-border text-text-muted">Inactive</span>
          )}
          <RefillBadge refillDate={medication.refill_date} />
        </div>
      </div>
    </button>
  )
}

export const MedicationsPage = () => {
  const { user, isAdmin, isLimited } = useAuth()
  const { data: members = [] }        = useMembers()
  const { data: medications = [], isLoading, create, update, remove } = useMedications()

  const [activeTab,  setActiveTab]  = useState('active')
  const [memberTab,  setMemberTab]  = useState('all')
  const [showForm,   setShowForm]   = useState(false)
  const [editing,    setEditing]    = useState(null)
  const [viewing,    setViewing]    = useState(null)

  const realMembers = members.filter((m) => !m.isShared)

  const hasRefillAlert = useMemo(() =>
    medications.some((m) =>
      m.is_active && m.refill_date &&
      differenceInDays(parseISO(m.refill_date), new Date()) <= 7
    ),
  [medications])

  const filtered = useMemo(() => {
    let list = medications
    if (!isAdmin) list = list.filter((m) => m.member_id === user?.id)
    if (memberTab !== 'all') list = list.filter((m) => m.member_id === memberTab)
    list = list.filter((m) => activeTab === 'active' ? m.is_active : !m.is_active)
    return list
  }, [medications, isAdmin, user, memberTab, activeTab])

  const handleEdit = (med) => {
    setViewing(null)
    setEditing(med)
    setShowForm(true)
  }

  const handleSave = async (payload) => {
    if (editing) {
      await update.mutateAsync({ id: editing.id, ...payload })
    } else {
      await create.mutateAsync(payload)
    }
  }

  if (isLoading) return <div className="px-4 py-5"><SkeletonList count={4} /></div>

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold text-text-primary">Medications</h1>
          {hasRefillAlert && (
            <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
          )}
        </div>
        {!isLimited && (
          <button
            onClick={() => { setEditing(null); setShowForm(true) }}
            className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Add
          </button>
        )}
      </div>

      {/* Member tabs — admins with multiple members */}
      {isAdmin && realMembers.length > 1 && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          <button
            onClick={() => setMemberTab('all')}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
              memberTab === 'all' ? 'bg-primary text-white' : 'bg-surface border border-border text-text-secondary',
            )}
          >
            All
          </button>
          {realMembers.map((m) => (
            <button
              key={m.user_id}
              onClick={() => setMemberTab(m.user_id)}
              className={cn(
                'px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
                memberTab === m.user_id ? 'bg-primary text-white' : 'bg-surface border border-border text-text-secondary',
              )}
            >
              {m.profiles?.display_name}
            </button>
          ))}
        </div>
      )}

      {/* Active / Inactive toggle */}
      <div className="flex gap-2 mb-4">
        {['active', 'inactive'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors',
              activeTab === tab ? 'bg-primary text-white' : 'bg-surface border border-border text-text-secondary',
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          type="medications"
          title={activeTab === 'active' ? 'No active medications' : 'No inactive medications'}
          description={activeTab === 'active' ? 'Track prescriptions and refill dates for household members.' : ''}
        />
      ) : (
        <div className="space-y-2">
          {filtered.map((med) => (
            <MedicationCard
              key={med.id}
              medication={med}
              onClick={() => setViewing(med)}
            />
          ))}
        </div>
      )}

      {showForm && (
        <MedicationForm
          medication={editing}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditing(null) }}
        />
      )}
      {viewing && (
        <MedicationDetail
          medication={viewing}
          onEdit={handleEdit}
          onDelete={remove.mutateAsync}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  )
}
